"use client";

import React, { useMemo, useState } from "react";
import { Loader2, Activity, BarChart3, DollarSign, Map, Users, UserX, Info } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";

import USMapSVG, { STATE_NAMES } from "@/app/(authenticated)/settings/_modules/components/USMapSVG";
import type { StateData } from "@/app/(authenticated)/settings/_modules/components/USMapSVG";
import { useGetAgencyPolicyStatsByState, useGetAgencyLeadStatsByState, useGetDialEfficiency } from "../../hooks/use-hook";

interface AgencyPolicyMapProps {
  agencyId: string;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

type MapView = "policies" | "leads" | "efficiency";

export default function AgencyPolicyMap({
  agencyId,
  period = '6-months',
  startDate,
  endDate,
}: AgencyPolicyMapProps) {
  const [view, setView] = useState<MapView>("policies");

  const { data: policyData, isLoading: policyLoading, error: policyError } = useGetAgencyPolicyStatsByState(agencyId, period, startDate, endDate);
  const { data: leadData, isLoading: leadLoading, error: leadError } = useGetAgencyLeadStatsByState(agencyId, period, startDate, endDate);
  const { data: effData, isLoading: effLoading, isError: effError } = useGetDialEfficiency(agencyId, period, startDate, endDate);

  const isLoading = view === "policies" ? policyLoading : view === "leads" ? leadLoading : effLoading;
  const error = view === "policies" ? policyError : view === "leads" ? leadError : effError ? new Error("failed") : null;

  // Policy view data
  const policyStateData: StateData[] = useMemo(() => {
    if (!policyData) return [];
    return Array.isArray(policyData) ? policyData : [];
  }, [policyData]);

  // Policies whose lead has no state can't be plotted. The backend returns them
  // as a sentinel `state:'Unmapped'` row so totals stay agency-wide-correct;
  // we keep it OUT of the map coloring / state count / color scale.
  const mappablePolicyData = useMemo(
    () => policyStateData.filter((d) => d.state && d.state !== "Unmapped"),
    [policyStateData]
  );

  const policyStats = useMemo(() => {
    // Totals sum EVERY row (incl. Unmapped) → equals the true agency-wide total.
    const totalPolicies = policyStateData.reduce((sum, d) => sum + d.count, 0);
    const issuedPremium = policyStateData.reduce(
      (sum, d) => sum + Number(d.issuedPremium || 0),
      0,
    );
    const totalPremium = policyStateData.reduce(
      (sum, d) => sum + Number(d.totalPremium || 0),
      0
    );
    const unmapped = policyStateData.find((d) => d.state === "Unmapped");
    // Map-only aggregates use just the plottable states.
    const activeStates = mappablePolicyData.filter((d) => d.count > 0).length;
    const maxCount = Math.max(...mappablePolicyData.map((d) => d.count), 1);
    return { totalPolicies, activeStates, totalPremium, issuedPremium, maxCount, unmappedCount: unmapped?.count || 0 };
  }, [policyStateData, mappablePolicyData]);

  // Lead view data — map to StateData format for USMapSVG (drop the Unmapped
  // sentinel so it never colors / skews the color scale).
  const leadStateData: StateData[] = useMemo(() => {
    if (!leadData) return [];
    const arr = Array.isArray(leadData) ? leadData : [];
    return arr
      .filter((d: any) => d.state && d.state !== "Unmapped")
      .map((d: any) => ({
        state: d.state,
        count: d.count,
        totalPremium: 0,
        topCarrier: `${d.unassignedCount} unassigned / ${d.assignedCount} assigned`,
      }));
  }, [leadData]);

  const leadStats = useMemo(() => {
    const rawArr = Array.isArray(leadData) ? leadData : [];
    const mappable = rawArr.filter((d: any) => d.state && d.state !== "Unmapped");
    // Totals include the Unmapped row → true agency-wide counts.
    const totalLeads = rawArr.reduce((sum: number, d: any) => sum + d.count, 0);
    const unassigned = rawArr.reduce((sum: number, d: any) => sum + d.unassignedCount, 0);
    const unmapped = rawArr.find((d: any) => d.state === "Unmapped");
    const activeStates = mappable.filter((d: any) => d.count > 0).length;
    const maxCount = Math.max(...mappable.map((d: any) => d.count), 1);
    return { totalLeads, activeStates, unassigned, maxCount, unmappedCount: unmapped?.count || 0 };
  }, [leadData]);

  // Dial-efficiency view — colors by PREMIUM SOLD per 100 dials (darker =
  // that state turns dials into money with less effort). Coloring by
  // appointments left sales-heavy states (FL) gray with a dead tooltip while
  // the Best-ROI tile crowned them — color and tile now tell one story.
  // Only 2-letter plottable states; free-text state values still count in
  // the Dial Efficiency tab.
  const effStateRows = useMemo(
    () => (effData?.byState || []).filter((r) => r.state in STATE_NAMES),
    [effData],
  );
  const effStateData: StateData[] = useMemo(
    () =>
      effStateRows.map((r) => ({
        state: r.state,
        count: Math.round(Number(r.premiumPer100Dials || 0)),
        totalPremium: 0,
        topCarrier: `${r.dials.toLocaleString()} dials · ${r.appointments} appts · ${r.policiesSold ?? 0} policies sold${
          r.dialsPerSale != null ? ` · ${r.dialsPerSale} dials per policy sold` : ""
        }`,
      })),
    [effStateRows],
  );
  const effStats = useMemo(() => {
    const totalDials = effStateRows.reduce((sum, r) => sum + r.dials, 0);
    const totalPremium = effStateRows.reduce((sum, r) => sum + Number(r.premium || 0), 0);
    const withSales = effStateRows.filter((r) => (r.policiesSold ?? 0) >= 2 && r.dialsPerSale != null);
    const best = withSales.length
      ? withSales.reduce((a, b) => (b.dialsPerSale! < a.dialsPerSale! ? b : a))
      : null;
    const maxCount = Math.max(...effStateData.map((d) => d.count), 1);
    return {
      totalDials,
      premiumPer100:
        totalDials > 0 ? Math.round((totalPremium / totalDials) * 100) : 0,
      best,
      maxCount,
    };
  }, [effStateRows, effStateData]);

  const currentStateData =
    view === "policies" ? mappablePolicyData : view === "leads" ? leadStateData : effStateData;
  const currentMaxCount =
    view === "policies" ? policyStats.maxCount : view === "leads" ? leadStats.maxCount : effStats.maxCount;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-red-400 text-sm">Failed to load map data</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl p-6">
      {/* View Toggle */}
      <div className="flex items-center justify-center mb-4">
        <div className="inline-flex rounded-lg border border-border bg-muted/30 p-1">
          <button
            onClick={() => setView("policies")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === "policies"
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Policies
          </button>
          <button
            onClick={() => setView("leads")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === "leads"
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Leads
          </button>
          <button
            onClick={() => setView("efficiency")}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              view === "efficiency"
                ? "bg-white text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Dial Efficiency
          </button>
        </div>
      </div>

      {/* Stats bar top */}
      {view === "policies" ? (
        <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <BarChart3 className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Total Policies
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {policyStats.totalPolicies.toLocaleString()}
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Map className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Active States
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {policyStats.activeStates}
            </p>
          </div>

          {/* Sold and production are their own tiles rather than one figure
              trailing the other — "$336,870 $169,844 issued" gave no clue which
              was which, and the label named only the larger one. */}
          <div className="rounded-lg border border-border bg-muted/50 p-3 text-center">
            <div className="mb-1 flex items-center justify-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Total Premium Sold
              </span>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What counts as premium sold"
                      className="inline-flex cursor-help items-center align-middle"
                    >
                      <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground">
                    Only policies marked sold and approved.
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </div>
            {/* div, not p: TooltipContent renders a div, and a div inside a p
                is invalid HTML — React reparents it and the hydration mismatch
                throws in the console. */}
            <div className="text-xl font-bold text-emerald-600">
              {formatCurrency(policyStats.issuedPremium)}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/50 p-3 text-center">
            <div className="mb-1 flex items-center justify-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Total Production
              </span>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="What counts as total production"
                      className="inline-flex cursor-help items-center align-middle"
                    >
                      <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-64 text-left text-xs font-normal normal-case leading-snug tracking-normal text-popover-foreground">
                    Everything marked sold and approved as well as in underwriting.
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </div>
            <div className="text-xl font-bold text-foreground">
              {formatCurrency(policyStats.totalPremium)}
            </div>
            {policyStats.unmappedCount > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Agency-wide · {policyStats.unmappedCount} not mapped (no state)
              </p>
            )}
          </div>
        </div>
      ) : view === "efficiency" ? (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Activity className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Dials (mapped)
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{effStats.totalDials.toLocaleString()}</p>
          </div>
          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <BarChart3 className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Premium Sold / 100 Dials
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">{formatCurrency(effStats.premiumPer100)}</p>
          </div>
          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Map className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Best ROI State
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {effStats.best ? effStats.best.state : "—"}
            </p>
            {effStats.best && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                {effStats.best.dialsPerSale} dials per policy sold
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Total Leads
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {leadStats.totalLeads.toLocaleString()}
            </p>
            {leadStats.unmappedCount > 0 && (
              <p className="mt-1 text-[11px] text-muted-foreground">
                Agency-wide · {leadStats.unmappedCount} not mapped (no state)
              </p>
            )}
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Map className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Active States
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {leadStats.activeStates}
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <UserX className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Unassigned
              </span>
            </div>
            <p className="text-xl font-bold text-foreground">
              {leadStats.unassigned.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Map */}
      <USMapSVG
        data={currentStateData}
        maxCount={currentMaxCount}
        itemLabel={view === "leads" ? "lead" : "policy"}
        formatValue={
          view === "efficiency"
            ? (d) => `${formatCurrency(d.count)} premium sold per 100 dials`
            : undefined
        }
      />

      {/* Color legend */}
      <div className="flex items-center justify-center gap-6 mt-3">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: "#e5e7eb" }} />
          <span className="text-xs text-muted-foreground">
            {view === "policies" ? "No policies" : view === "leads" ? "No leads" : "No premium sold"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary) 30%, transparent)" }}
          />
          <span className="text-xs text-muted-foreground">Low</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary) 65%, transparent)" }}
          />
          <span className="text-xs text-muted-foreground">Medium</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: "var(--primary)" }}
          />
          <span className="text-xs text-muted-foreground">High</span>
        </div>
      </div>
    </div>
  );
}
